import { useState } from "react";
import { toast } from "sonner";
import { Eye, X } from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "@/components/ui/dialog";
import { createWatch, errText } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

// Reasonable defaults per country. Users can override.
const TZ_BY_COUNTRY = {
    au: "Australia/Sydney",
    us: "America/New_York",
    uk: "Europe/London",
    ca: "America/Toronto",
    nz: "Pacific/Auckland",
    in: "Asia/Kolkata",
    ie: "Europe/Dublin",
    sg: "Asia/Singapore",
    ae: "Asia/Dubai",
    de: "Europe/Berlin",
    fr: "Europe/Paris",
};

const COMMON_TZS = [
    "Australia/Sydney",
    "Australia/Melbourne",
    "Australia/Brisbane",
    "Australia/Perth",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Dubai",
    "Asia/Tokyo",
    "America/New_York",
    "America/Chicago",
    "America/Los_Angeles",
    "America/Toronto",
    "Europe/London",
    "Europe/Dublin",
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Madrid",
    "Pacific/Auckland",
    "UTC",
];

export const CreateWatchDialog = ({ onCreated, trigger, initialSearch }) => {
    const [open, setOpen] = useState(false);
    const [businessType, setBusinessType] = useState(initialSearch?.business_type || "");
    const [city, setCity] = useState(initialSearch?.city || "");
    const [country, setCountry] = useState(initialSearch?.country || "au");
    const [timezone, setTimezone] = useState(TZ_BY_COUNTRY[initialSearch?.country || "au"] || "UTC");
    const [label, setLabel] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!businessType.trim() || !city.trim()) {
            toast.error("Business type and city are required");
            return;
        }
        setSubmitting(true);
        try {
            const w = await createWatch({
                business_type: businessType.trim(),
                city: city.trim(),
                country,
                timezone,
                label: label.trim(),
            });
            toast.success(`Daily Ad Watch enabled · fires at 7/10/15/19/21 (${timezone})`);
            onCreated?.(w);
            setOpen(false);
        } catch (err) {
            toast.error(errText(err, "Failed to create watch"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <button
                        data-testid="open-create-watch"
                        className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                    >
                        <Eye size={13} weight="bold" />
                        Enable Daily Ad Watch
                    </button>
                )}
            </DialogTrigger>
            <DialogContent className="max-w-lg rounded-none border-2 border-ink p-0 bg-white">
                <DialogHeader className="p-5 border-b-2 border-ink">
                    <DialogTitle className="font-display text-xl font-black tracking-tighter">
                        Enable Daily Ad Watch
                    </DialogTitle>
                    <DialogDescription className="mono-label mt-1">
                        Auto-runs at 7 AM · 10 AM · 3 PM · 7 PM · 9 PM · Report after 9 PM
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div>
                        <label className="mono-label block mb-1.5">Business type</label>
                        <input
                            data-testid="watch-business-type"
                            value={businessType}
                            onChange={(e) => setBusinessType(e.target.value)}
                            placeholder="e.g. end of lease cleaning"
                            className="w-full h-9 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mono-label block mb-1.5">City / Suburb</label>
                            <input
                                data-testid="watch-city"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="Wollongong"
                                className="w-full h-9 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm"
                                required
                            />
                        </div>
                        <div>
                            <label className="mono-label block mb-1.5">Country</label>
                            <select
                                data-testid="watch-country"
                                value={country}
                                onChange={(e) => {
                                    setCountry(e.target.value);
                                    setTimezone(TZ_BY_COUNTRY[e.target.value] || "UTC");
                                }}
                                className="w-full h-9 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm"
                            >
                                {COUNTRIES.map((c) => (
                                    <option key={c.code} value={c.code}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="mono-label block mb-1.5">Timezone (schedule fires in this zone)</label>
                        <select
                            data-testid="watch-timezone"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="w-full h-9 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm font-mono"
                        >
                            {[...new Set([timezone, ...COMMON_TZS])].map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mono-label block mb-1.5">Label (optional)</label>
                        <input
                            data-testid="watch-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Client X - Wollongong campaign"
                            className="w-full h-9 px-3 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-sm"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                        >
                            <X size={11} weight="bold" />
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="watch-submit"
                            disabled={submitting}
                            className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-4 h-9 flex items-center gap-1.5 disabled:opacity-40"
                        >
                            {submitting ? "Enabling…" : "Enable Watch"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
